import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import translate from 'utils/fnTranslate';
import { styled } from '../ThemeConfigConnector/ThemeConfigConnnector';
import { PinInput } from './InputPin';

type PropTypes = {
    positions?: number;
    isInvalid?: boolean;
    reset?: boolean;
    ariaLabel?: string;
    onChange?(values: string[]): void;
    onComplete?(values: string[]): void;
};

const PinInputsGroup = styled.div<PropTypes>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0 17px;
    cursor: pointer;
`;

const STEP_BACK_KEYCODES = [8, 46]; // backspace, delete

const DIGIT_LABEL_KEY = 'ARIA_PIN_DIGIT';

const DIGIT_LABEL_FALLBACK: Record<string, string> = {
    en: 'Digit %index% of %count%',
    et: '%index%. number %count%-st',
    ru: 'Цифра %index% из %count%',
};

const getDigitLabel = (index: number, count: number): string => {
    const vars = { index: index + 1, count };
    const translation = translate(DIGIT_LABEL_KEY, vars);

    if (translation !== DIGIT_LABEL_KEY) return translation;

    const template = DIGIT_LABEL_FALLBACK[document.documentElement.lang] ?? DIGIT_LABEL_FALLBACK.et;

    return template.replace('%index%', `${vars.index}`).replace('%count%', `${vars.count}`);
};

export const PinInputGroup = forwardRef<HTMLDivElement, PropTypes>(({ positions = 4, onComplete, reset, ariaLabel }, ref) => {
    let pinsArray = Array(positions).fill('');
    const [pins, setPins] = useState<string[]>(pinsArray);
    const [currentPinIndex, setCurrentPinIndex] = useState<number>(0);
    const groupRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => groupRef.current as HTMLDivElement);

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.match(/[0-9]/)) {
            pinsArray = [...pins];
            pinsArray[currentPinIndex] = e.target.value;
            setPins(pinsArray);

            const allFilled = pinsArray.every((pin) => pin !== '');

            if (allFilled) {
                onComplete(pinsArray);
            } else if (pinsArray[currentPinIndex] === e.target.value && currentPinIndex < positions - 1) {
                setCurrentPinIndex(currentPinIndex + 1);
            }
        }
    };

    const onInputFocus = (index: number) => {
        setCurrentPinIndex(index);
    };

    const onGroupMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).tagName === 'INPUT') return;

        e.preventDefault();

        const inputs = groupRef.current?.querySelectorAll<HTMLInputElement>('[data-pin-input]');
        inputs?.[currentPinIndex]?.focus();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (STEP_BACK_KEYCODES.includes(e.keyCode)) {
            if (currentPinIndex === 0 && pins[0] === '') return;

            e.preventDefault();
            e.stopPropagation();

            pinsArray = [...pins];
            pinsArray[currentPinIndex] = '';
            setPins(pinsArray);

            if (currentPinIndex > 0) {
                setCurrentPinIndex(currentPinIndex - 1);
            }
        }
    };

    useEffect(() => {
        if (reset) {
            setPins(Array(positions).fill(''));
            setCurrentPinIndex(0);
        }
    }, [reset]);

    return (
        <PinInputsGroup ref={groupRef} onMouseDown={onGroupMouseDown}>
            <div role="group" aria-label={ariaLabel}>
                {pins.map((_, i) => (
                    <PinInput
                        data-test-id="header-settings-pinprotect-enter-pin"
                        key={i}
                        isActive={currentPinIndex === i}
                        onChange={onInputChange}
                        onKeyDown={onKeyDown}
                        onFocus={() => onInputFocus(i)}
                        autoComplete="new-password"
                        value={pins[i]}
                        index={i}
                        label={getDigitLabel(i, positions)}
                    />
                ))}
            </div>
        </PinInputsGroup>
    );
});

PinInputGroup.displayName = 'PinInputGroup';
